<script setup>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { DxList } from 'devextreme-vue/list'

import { getI18n } from '@/plugins/i18n'

import { useODataSource } from '@/composables/data-grid'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'
import { useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'

import { planningApi } from '@/apis/planning.api'
import { useWebSiteStore } from '@/stores/web-site.store'

import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import GroupDetail from '@/components/shared/GroupDetail.vue'
import GroupEdit from '@/components/shared/GroupEdit.vue'
import GroupListItem from '@/components/shared/GroupListItem.vue'

const i18n = getI18n()
const route = useRoute()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()

const listContainer = ref(null)
let listInstance = null

const isEmptyDataSource = ref(false)
const selectedItem = ref(null)
const editItem = ref(null)

webSiteStore.fabActions = [
  {
    id: 'add',
    icon: 'mdi-plus',
    onClick: () => { editItem.value = {} },
  },
]

const dataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.groupsUrl, {
  onChanged: () => {
    isEmptyDataSource.value = dataSource.totalCount() === 0
  },
  searchExpr: 'search_field',
})

const onDetailUpdate = () => {
  selectedItem.value = null
  dataSource.pageIndex(0)
  dataSource.reload()
}

const onEditItemCreated = async () => {
  editItem.value = null
  webSiteStore.activateUpdatedDateSort(route.name)
  dataSource.sort(webSiteStore.buildSortOrderBy(route.name))
  dataSource.pageIndex(0)
  const loaded = await dataSource.reload()
  await highlightRowAsync(loaded?.[0]?.id, listContainer)
  listInstance?.repaint()
}

const onEditItemChanged = () => {
  editItem.value = null
  dataSource.reload()
}

useEntityChanges([entityChangeTypeEnum.GROUP], (change) => (change.created ? onEditItemCreated() : dataSource.reload()), () => editItem.value !== null)
</script>
<template>
  <div>
    <filter-sort-selector :data-source="dataSource" />
    <q-card flat>
      <q-card-section ref="listContainer" class="q-pa-none">
        <q-dialog
          maximized
          :model-value="selectedItem !== null"
        >
          <group-detail
            :model-value="selectedItem"
            @cancel="onDetailUpdate"
            @update="onDetailUpdate"
          />
        </q-dialog>
        <q-dialog
          :maximized="isMobile"
          :model-value="editItem !== null"
          class="dialog-sm"
        >
          <group-edit
            v-if="editItem !== null"
            :model-value="editItem"
            @update="onEditItemCreated"
            @cancel="onEditItemChanged"
            @delete="onEditItemChanged"
          />
        </q-dialog>
        <dx-list
          v-show="!isEmptyDataSource"
          :data-source="dataSource"
          height="calc(100vh - 8rem)"
          page-load-mode="scrollBottom"
          class="events-list"
          item-template="item"
          selection-mode="none"
          :active-state-enabled="false"
          @initialized="e => listInstance = e.component"
        >
          <template #item="{ data: group }">
            <q-item>
              <group-list-item
                :key="group.id"
                :group="group"
                class="q-pa-sm"
                :class="{ 'row-highlight': group.id === highlightedRowId }"
                dense
                clickable
                @click="selectedItem = group"
              />
            </q-item>
          </template>
        </dx-list>
        <div
          v-show="isEmptyDataSource"
          class="text-center text-grey-6 q-pa-xl"
        >
          {{ i18n.t('common.noData') }}
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>
